import { UserRole } from "../interfaces/user.interface";

/**
 * Permission types that can be assigned to routes
 */
export type Permission =
  // Module permissions
  | "module:planning"
  | "module:sales"
  | "module:analytics"
  | "module:admin"
  // Planning module pages
  | "page:planning:projects"
  | "page:planning:planning"
  | "page:planning:schedule"
  | "page:planning:activities"
  | "page:planning:stamps"
  | "page:planning:timesheets"
  // Sales module pages
  | "page:sales:quotes"
  | "page:sales:contracts"
  | "page:sales:contacts"
  // Analytics module pages
  | "page:analytics:margin"
  | "page:analytics:production-analysis"
  | "page:analytics:workload"
  | "page:analytics:sal"
  | "page:analytics:timesheets"
  // Admin module pages
  | "page:admin:price-lists"
  | "page:admin:standard-tasks"
  | "page:admin:users";

/**
 * Centralized permissions configuration
 * Define which roles have access to which permissions
 *
 * IMPORTANT: Edit this object to change who can see what
 *
 * Rules:
 * - ADMIN has access to everything (checked automatically)
 * - If a role is listed in a permission array, they have access
 * - If not listed, they don't have access (unless ADMIN)
 * - Module access is required to see any pages within that module
 */
export const PERMISSIONS_CONFIG: Record<Permission, UserRole[]> = {
  // ============================================================
  // MODULES - Define which roles can access each module
  // ============================================================

  "module:planning": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
    UserRole.EMPLOYEE,
    UserRole.SALES, // Sales can see planning module (only activities page)
    UserRole.ADMINISTRATION, // Administration: activities, projects (read-only), timesheets
    UserRole.APPROVER, // Timbratore can see planning module
    UserRole.EXTERNAL_COLLABORATOR, // External collaborator can see planning module (only activities page)
  ],

  "module:sales": [
    UserRole.ADMIN,
    UserRole.SALES,
    UserRole.PROJECT_MANAGER,
    UserRole.ADMINISTRATION, // Administration has full sales access
  ],

  "module:analytics": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
  ],

  "module:admin": [
    UserRole.ADMIN, // Only admin can access admin module
  ],

  // ============================================================
  // PLANNING MODULE PAGES
  // ============================================================

  "page:planning:projects": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
    UserRole.ADMINISTRATION, // Sees all projects, read-only (enforced in UI)
  ],

  "page:planning:planning": [
    UserRole.ADMIN,
    UserRole.TEAM_LEAD,
    UserRole.PROJECT_MANAGER,
  ],

  "page:planning:schedule": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
  ],

  "page:planning:activities": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
    UserRole.EMPLOYEE,
    UserRole.SALES, // Sales can see activities page
    UserRole.ADMINISTRATION, // Administration sees activities like an employee (own subtasks)
    UserRole.EXTERNAL_COLLABORATOR, // External collaborator can ONLY see activities page
  ],

  "page:planning:stamps": [
    UserRole.ADMIN,
    UserRole.APPROVER, // Timbratore sees only stamps page
  ],

  "page:planning:timesheets": [
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.TEAM_LEAD,
    UserRole.EMPLOYEE,
    UserRole.EXTERNAL_COLLABORATOR,
    UserRole.ADMINISTRATION, // Sees all timesheets, edits only their own (enforced in logic)
  ],

  // ============================================================
  // SALES MODULE PAGES
  // ============================================================

  // Project Manager added so the PM can see/manage quotes and
  // contracts of their own projects. The actual visibility is
  // restricted to the PM's entities at the data layer: list state
  // services append `projectManagerId=<loggedUser.sub>` to the
  // requests when the logged user is a PM (and not also admin /
  // sales), and the sales BE filters the result.
  "page:sales:quotes": [
    UserRole.ADMIN,
    UserRole.SALES,
    UserRole.PROJECT_MANAGER,
    UserRole.ADMINISTRATION,
  ],

  "page:sales:contracts": [
    UserRole.ADMIN,
    UserRole.SALES,
    UserRole.PROJECT_MANAGER,
    UserRole.ADMINISTRATION,
  ],

  "page:sales:contacts": [
    UserRole.ADMIN,
    UserRole.SALES,
    UserRole.PROJECT_MANAGER,
    UserRole.ADMINISTRATION,
  ],

  // ============================================================
  // ANALYTICS MODULE PAGES
  // ============================================================

  "page:analytics:margin": [UserRole.ADMIN],

  "page:analytics:production-analysis": [UserRole.ADMIN, UserRole.TEAM_LEAD],

  "page:analytics:workload": [UserRole.ADMIN],

  "page:analytics:sal": [UserRole.ADMIN, UserRole.PROJECT_MANAGER],

  "page:analytics:timesheets": [UserRole.ADMIN],

  // ============================================================
  // ADMIN MODULE PAGES
  // ============================================================

  "page:admin:price-lists": [UserRole.ADMIN],

  "page:admin:standard-tasks": [UserRole.ADMIN],

  "page:admin:users": [UserRole.ADMIN],
};

/**
 * Helper function to get all permissions for a given role
 * Automatically includes all permissions if role is ADMIN
 *
 * @param roles - Array of user roles
 * @returns Array of permissions the user has access to
 */
export function getPermissionsForRoles(roles: UserRole[]): Permission[] {
  // Admin has access to everything
  if (roles.includes(UserRole.ADMIN)) {
    return Object.keys(PERMISSIONS_CONFIG) as Permission[];
  }

  // Collect all permissions where at least one of user's roles is allowed
  const permissions = new Set<Permission>();

  for (const [permission, allowedRoles] of Object.entries(PERMISSIONS_CONFIG)) {
    if (roles.some((role) => allowedRoles.includes(role))) {
      permissions.add(permission as Permission);
    }
  }

  return Array.from(permissions);
}

/**
 * Check if any of the user's roles has the specified permission
 *
 * @param roles - Array of user roles
 * @param permission - Permission to check
 * @returns true if user has the permission
 */
export function hasPermission(
  roles: UserRole[],
  permission: Permission,
): boolean {
  // Admin has access to everything
  if (roles.includes(UserRole.ADMIN)) {
    return true;
  }

  const allowedRoles = PERMISSIONS_CONFIG[permission];
  return roles.some((role) => allowedRoles.includes(role));
}

/**
 * Get module permission from module ID
 */
export function getModulePermission(moduleId: string): Permission {
  return `module:${moduleId}` as Permission;
}

/**
 * Get page permission from module ID and page ID
 */
export function getPagePermission(
  moduleId: string,
  pageId: string,
): Permission {
  return `page:${moduleId}:${pageId}` as Permission;
}
